import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Button, Container, Typography, Paper, Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLanguage } from "../context/LanguageContext";
import AuthShell from "../components/AuthShell";

interface Section {
  titleKey: string;
  contentKeys: string[];
}

const SECTIONS: Section[] = [
  { titleKey: "terms.acceptance.title", contentKeys: ["terms.acceptance.content1", "terms.acceptance.content2", "terms.acceptance.footer"] },
  { titleKey: "terms.dataCollection.title", contentKeys: ["terms.dataCollection.intro", "terms.dataCollection.item1", "terms.dataCollection.item2", "terms.dataCollection.item3", "terms.dataCollection.item4", "terms.dataCollection.item5"] },
  { titleKey: "terms.advertising.title", contentKeys: ["terms.advertising.content1", "terms.advertising.content2", "terms.advertising.content3"] },
  { titleKey: "terms.userContent.title", contentKeys: ["terms.userContent.intro", "terms.userContent.prohibited1", "terms.userContent.prohibited2", "terms.userContent.prohibited3", "terms.userContent.prohibited4", "terms.userContent.prohibited5"] },
  { titleKey: "terms.privacy.title", contentKeys: ["terms.privacy.content1", "terms.privacy.content2"] },
  { titleKey: "terms.thirdParty.title", contentKeys: ["terms.thirdParty.content"] },
  { titleKey: "terms.intellectual.title", contentKeys: ["terms.intellectual.content1", "terms.intellectual.content2"] },
  { titleKey: "terms.termination.title", contentKeys: ["terms.termination.content"] },
  { titleKey: "terms.liability.title", contentKeys: ["terms.liability.content"] },
  { titleKey: "terms.changes.title", contentKeys: ["terms.changes.content"] },
  { titleKey: "terms.law.title", contentKeys: ["terms.law.content"] },
  { titleKey: "terms.contact.title", contentKeys: ["terms.contact.intro"] },
];

export default function TermsAndConditions() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Terms always opens in a new tab from Signup, so back = close or go to signup
  const handleBack = () => navigate("/signup");

  const headerRight = (
    <Button
      size="small"
      variant="text"
      startIcon={<ArrowBackIcon sx={{ fontSize: "1rem" }} />}
      onClick={handleBack}
      sx={{
        color: "rgba(255,255,255,0.85)",
        fontWeight: 600,
        textTransform: "none",
        fontSize: "0.82rem",
        "&:hover": { color: "white" },
      }}
    >
      {t("terms.back")}
    </Button>
  );

  return (
    <AuthShell rightContent={headerRight}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: "16px" }}>
          <Typography variant="h4" fontWeight={800} gutterBottom sx={{ color: "#1A1A2E", letterSpacing: "-0.02em" }}>
            {t("terms.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {t("terms.intro.lastUpdated")}
          </Typography>
          <Typography variant="body1" mb={3} lineHeight={1.7}>
            {t("terms.intro.welcome")}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {SECTIONS.map((section, idx) => (
            <Box key={idx} sx={{ mb: 4 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: "#1A1A2E" }}>
                {idx + 1}. {t(section.titleKey)}
              </Typography>
              {section.contentKeys.map((key, i) => (
                <Typography key={i} variant="body2" color="text.secondary" mb={1.5} lineHeight={1.7}>
                  {t(key)}
                </Typography>
              ))}
            </Box>
          ))}

          <Divider sx={{ mb: 3 }} />
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            {t("terms.back")}
          </Button>
        </Paper>
      </Container>
    </AuthShell>
  );
}
